/** Returns the public TCGplayer URL for a card whose TCGplayer market price we show (#533,
 *  TRO-346).
 *
 *  Deep-links the exact product when the catalog carries a `tcgplayer_product_id` (scan candidates
 *  and printings do). Otherwise the identity is a Scrydex id that cannot become a product URL, so
 *  it searches by name and set; TCGplayer's search does not index the bare collector number, so
 *  name plus number finds nothing. Falls back to name only when the set is unknown. Works across
 *  games. */
export function tcgplayerUrl(
  name: string,
  setName?: string | null,
  productId?: number | null,
): string {
  if (productId != null) return `https://www.tcgplayer.com/product/${productId}`;
  const q = [name, setName].filter(Boolean).join(" ");
  return `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(q)}`;
}

// The origin is matched whole, which rejects both `tcgplayer.com.evil.test` and the
// userinfo trick `https://tcgplayer.com@evil.test/product/1`. `[0-9]` rather than `\d` mirrors the
// server rule: Python's `\d` also matches Unicode decimal digits, and this side's must not diverge.
const TCGPLAYER_ORIGIN = /^https:\/\/(?:[a-z0-9-]+\.)*tcgplayer\.com(?::[0-9]+)?$/i;
const TCGPLAYER_PRODUCT = /(?:^|\/)product\/([0-9]+)(?:\/|$)/;

/** Returns the TCGplayer product id `url` names, or `null` when it names none (TRO-371).
 *
 *  A catalog contribution identifies its evidence by a pasted product URL. This side decides
 *  whether the submit button is enabled and the server decides whether the payload is accepted, so
 *  both ask the same way. The rule lives in `backend/src/trovie/domain/tcg/tcgplayer_url.py` and
 *  its cases in `shared/fixtures/tcgplayer-product-url.json`, which both suites execute (root
 *  `AGENTS.md`, "One rule, two languages"). A `null` is a refusal: only https, only tcgplayer.com
 *  or a subdomain, and only `product/<id>` in the path. The URL is kept as evidence and shown to a
 *  reviewer, so an arbitrary link must not resolve. */

export function tcgplayerProductId(url: string): string | null {
  const trimmed = url.trim();
  const separator = trimmed.indexOf("://");
  if (separator < 0) return null;
  const rest = trimmed.slice(separator + 3);
  const slash = rest.indexOf("/");
  const authority = slash < 0 ? rest : rest.slice(0, slash);
  if (!TCGPLAYER_ORIGIN.test(`${trimmed.slice(0, separator)}://${authority}`)) return null;
  const path = (slash < 0 ? "" : rest.slice(slash)).split(/[?#]/)[0];
  return TCGPLAYER_PRODUCT.exec(path)?.[1] ?? null;
}
